import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { actions } from 'Data/ReduxViewModels/WorksheetGrid';
import { hasNonEmptyValue } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';
import { useHasChanged } from 'Lib/Hooks';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import moment from 'moment';
import { pick } from 'ramda';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTimeInput, Field, TextInput, TextOutput } from '../../Core';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import { FieldSet } from '../../Layout';
import { LaboratoryDropdown, StandaloneEquipmentDropdownField } from '../../List';
import { WorksheetGridRefreshButton, createFirstWorksheetSelector } from '../Core';
import WorksheetGridAdditionalDataInjectedPropsProvider from '../Core/WorksheetGridAdditionalDataInjectedPropsProvider';
import { WorksheetGridCommonDataInjectedPropsProvider } from '../Core/WorksheetGridCommonDataInjectedPropsProvider';
import { WorksheetGridQueryInjectedPropsProvider } from '../Core/WorksheetGridQueryInjectedPropsProvider';
import '../WorksheetGridControls.scss';
export const MoistureContentWetMassGridBalanceKey = 'WetMassEntry-Balance';

let MoistureContentWetMassWorksheetGridControls: React.FC = () => {
  const laboratoryId = useSelector((state) => state.worksheetGrid.query.params.LaboratoryId);
  const homeLaboratoryId = useSelector((state) => state.user.data.HomeLaboratory.Id);
  const balanceSelected = useSelector((state) =>
    hasNonEmptyValue(state.worksheetGrid.commonData['MoistureContentTest.Balance'])
  );
  const defaultLaboratoryBalance = useSelector(
    (state) => state.worksheetGrid.additionalData['LaboratoryDefaultBalance'],
    deepEquals
  );
  const balanceReadability = useSelector(
    pipe(
      (state) => (state.equipment.equipmentData as any)[MoistureContentWetMassGridBalanceKey]?.data,
      (data) => pick(['Readability', 'Uom'], data ?? {}) as any
    )
  );
  const dateTimeEntered = useSelector((state) =>
    hasNonEmptyValue(state.worksheetGrid.commonData['DryingTray.DateTimePlacedInOvenLocal'])
  );

  /**
   * Currently all worksheets in the grid must come from the same laboratory
   * We assume that the laboratory data attached to the first worksheet applies to all worksheets.
   * (In future, we should fetch laboratory data independently from the worksheet model
   * so we have this whether or not a test is returned)
   */
  const showConstantDryMassEntryFields = useSelector(
    pipe(createDryMassEntryOptionSelector(createFirstWorksheetSelector), (o) => o !== 'Never')
  );

  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(actions.cleanup());
    };
  }, [dispatch]);

  // Clear equipment when lab changes
  const hasLabChanged = useHasChanged(laboratoryId);
  useEffect(() => {
    if (hasLabChanged) {
      dispatch(
        actions.setCommonDataValue({
          key: 'MoistureContentTest.Balance',
          value: undefined,
          sync: true,
        })
      );
      dispatch(
        actions.setCommonDataValue({
          key: 'MoistureContentTest.Oven',
          value: undefined,
        })
      );
      dispatch(actions.setAdditionalDataValue({ key: 'SampleIdFilter', value: undefined }));
      dispatch(actions.getDefaultBalance({ laboratoryId: laboratoryId }));
    }
  }, [dispatch, hasLabChanged, laboratoryId]);

  const hasDefaultBalance = hasNonEmptyValue(defaultLaboratoryBalance);
  useEffect(() => {
    if (!hasDefaultBalance) {
      dispatch(
        actions.setCommonDataValue({
          key: 'MoistureContentTest.Balance',
          value: null,
          sync: true,
        })
      );
    }
  }, [dispatch, hasDefaultBalance]);

  const hasDefaultLaboratoryBalanceChanged = useHasChanged(defaultLaboratoryBalance);
  useEffect(() => {
    if ((hasDefaultLaboratoryBalanceChanged && !balanceSelected) || hasDefaultBalance) {
      dispatch(
        actions.setCommonDataValue({
          key: 'MoistureContentTest.Balance',
          value: defaultLaboratoryBalance,
          sync: true,
        })
      );
    }
  }, [dispatch, hasDefaultLaboratoryBalanceChanged, hasDefaultBalance, balanceSelected, defaultLaboratoryBalance]);

  // Set the date placed in oven as current time by default
  // Called on load or when this field is added/removed as different labs may or may not require this to be entered.
  useEffect(() => {
    dispatch(
      actions.setCommonDataValue({
        key: 'DryingTray.DateTimePlacedInOvenLocal',
        value: showConstantDryMassEntryFields
          ? moment().local().seconds(0).milliseconds(0).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : null,
        sync: true,
      })
    );
  }, [dispatch, showConstantDryMassEntryFields]);

  return (
    <>
      <div className="WorksheetGridControls">
        <div className="WorksheetGridControls-Query">
          <div className="WorksheetGridControls-HeaderFieldGroup">
            <WorksheetGridQueryInjectedPropsProvider>
              <Field
                horizontal
                label="Laboratory"
                input={<LaboratoryDropdown />}
                name="LaboratoryId"
                simpleValue
                isClearable={false}
                autoSelectSingleOption
                searchImmediately
                autoSelectedValue={homeLaboratoryId}
              />
            </WorksheetGridQueryInjectedPropsProvider>
            <WorksheetGridAdditionalDataInjectedPropsProvider>
              <Field horizontal input={<TextInput />} label="Filter by Sample ID" name="SampleIdFilter" />
            </WorksheetGridAdditionalDataInjectedPropsProvider>
          </div>
          <div className="WorksheetGridControls-RefreshButton">
            <WorksheetGridRefreshButton />
          </div>
        </div>
        <div className="WorksheetGridControls-CommonData">
          <div className="WorksheetGridControls-HeaderFieldGroup">
            <WorksheetGridCommonDataInjectedPropsProvider>
              <FieldSet horizontalLabelWidth="medium">
                <StandaloneEquipmentDropdownField
                  horizontal
                  label="Balance"
                  equipmentType="balance"
                  id={MoistureContentWetMassGridBalanceKey}
                  name="MoistureContentTest.Balance"
                  laboratoryId={laboratoryId}
                  disabled={!laboratoryId}
                  messages={
                    laboratoryId && !balanceSelected
                      ? [
                          {
                            severity: 'info',
                            message: 'A balance must be selected before masses can be entered.',
                          },
                        ]
                      : undefined
                  }
                />
                {balanceReadability && balanceReadability.Uom && balanceReadability.Readability && (
                  <Field
                    horizontal
                    input={<TextOutput />}
                    label={`Readable to Nearest (${balanceReadability.Uom})`}
                    value={balanceReadability?.Readability}
                  />
                )}
              </FieldSet>
              {showConstantDryMassEntryFields && (
                <Field
                  horizontal
                  label="Time In Oven"
                  input={<DateTimeInput />}
                  name="DryingTray.DateTimePlacedInOvenLocal"
                  dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
                  disabled={!laboratoryId}
                  messages={
                    laboratoryId && !dateTimeEntered
                      ? [
                          {
                            severity: 'info',
                            message: 'Date/time in oven must be entered before masses can be entered.',
                          },
                        ]
                      : undefined
                  }
                />
              )}
              <StandaloneEquipmentDropdownField
                horizontal
                label="Oven"
                equipmentType="oven"
                name="MoistureContentTest.Oven"
                laboratoryId={laboratoryId}
                disabled={!laboratoryId}
              />
            </WorksheetGridCommonDataInjectedPropsProvider>
          </div>
        </div>
      </div>
    </>
  );
};

MoistureContentWetMassWorksheetGridControls = withInjectedProps(MoistureContentWetMassWorksheetGridControls);

export default MoistureContentWetMassWorksheetGridControls;
export { MoistureContentWetMassWorksheetGridControls };
